 

import React, { createContext, useContext, useState, useEffect } from "react";
import { 
  addRackData, 
  getShedRack, 
  viewRackData, 
  updateRackData, 
  deleteRackData,
  editRackData, 
  getProcess,
  getOrderList
} from "../../../DriftRoutes/contextsApi";
import { useLoader } from "../LoaderContext";

const RackContext = createContext();

export const useRackData = () => useContext(RackContext);

export const RackProvider = ({ children }) => {
  const [racks, setRacks] = useState([]);
  const [rackSeats, setRackSeats] = useState({});
  const [shed, setShed] = useState([]);
  const [process, setProcess] = useState([]);
  const [orderList, setOrderList] = useState([]);
  const [shedLayouts, setShedLayouts] = useState({});
  const [triggerRefresh, setTriggerRefresh] = useState(false);

  const { showLoader, hideLoader } = useLoader();

  // Fetch all racks data
  const fetchAllRacks = async () => {
    try {
      showLoader();
      const response = await viewRackData();
      if (response?.status && response?.data) {
        const formattedRacks = response.data.map(rack => ({
          id: rack._id.toString(),
          _id: rack._id,
          laneName: rack.row_name,
          rackCount: rack.rack_total,
          perRack: rack.tiles_per_rack,
          size: rack.size,
          shed: rack.shed_id,
          totalBoxes: rack.tiles_per_box || 0,
          boxesPerRack: rack.boxes_per_rack || 0,
          
          shedDetails: rack.shed_details,
          rack_count: rack.rack_count || []
        }));
        setRacks(formattedRacks);
        
        // Initialize seats using actual rack_count data
        formattedRacks.forEach(rack => {
          initializeSeats(rack);
        });
        
        // Store shed layouts
        const layouts = {};
        formattedRacks.forEach(rack => {
          if (rack.shedDetails) {
            layouts[rack.shed] = {
              height: rack.shedDetails.height,
              width: rack.shedDetails.width,
              shed_name: rack.shedDetails.shed_name,
              _id: rack.shedDetails._id
            };
          }
        });
        setShedLayouts(layouts);
      }
      hideLoader();
    } catch (error) {
      console.error("Error fetching racks:", error);
      hideLoader();
    }
  };

  // Fetch sheds
  const fetchingShed = async () => {
    try {
      showLoader();
      const response = await getShedRack();
      if (response?.data) {
        setShed(response?.data);
      }
      hideLoader();
    } catch (error) {
      console.log("Error while fetching shed", error);
      hideLoader();
    }
  };

  // Fetch order list
  const fetchOrderList = async () => {
    try {
      showLoader();
      const response = await getOrderList();
      if (response.data) {
        setOrderList(response.data);
      }
      hideLoader();
    } catch (error) {
      console.log("Error while fetching order list", error);
      hideLoader();
    }
  };

  // Fetch process list
  const fetchingProcess = async () => {
    try {
      showLoader();
      const response = await getProcess();
      if (response?.data) {
        setProcess(response?.data);
      }
      hideLoader();
    } catch (error) {
      console.log("Error while fetching process", error);
      hideLoader();
    }
  };

  // Add rack with API
  const addRack = async (newRack) => {
    try {
      showLoader();
      const payload = {
        row_name: newRack.laneName,
        rack_total: parseInt(newRack.rackCount),
        tiles_per_rack: parseInt(newRack.perRack),
        size: newRack.size,
        tiles_per_box: parseInt(newRack.totalBoxes) || 0,
        boxes_per_rack: parseInt(newRack.boxesPerRack) || 0,
        shed_id: parseInt(newRack.shed),
         
      };

      const response = await addRackData(payload);
      if (response?.status) {
        await fetchAllRacks();
      }
      hideLoader();
      return response;
    } catch (error) {
      console.error("Error adding rack:", error);
      hideLoader();
      throw error;
    }
  };

  // Update rack with API
  const updateRack = async (id, updatedData) => {
    try {
      showLoader();
      const payload = {
        _id: parseInt(id),
        row_name: updatedData.laneName,
        rack_total: parseInt(updatedData.rackCount),
        tiles_per_rack: parseInt(updatedData.perRack),
        size: updatedData.size,
        tiles_per_box: parseInt(updatedData.totalBoxes) || 0,
        boxes_per_rack: parseInt(updatedData.boxesPerRack) || 0,
        shed_id: parseInt(updatedData.shed),
         
      };

      const response = await updateRackData(payload);
      if (response?.status) {
        await fetchAllRacks();
      }
      hideLoader();
      return response;
    } catch (error) {
      console.error("Error updating rack:", error);
      hideLoader();
      throw error;
    }
  };

  // Edit rack (fetch single rack)
  const editRack = async (id) => {
    try {
      showLoader();
      const response = await editRackData(id);
      hideLoader();
      if (response?.status && response?.data) {
        return {
          id: response.data._id.toString(),
          laneName: response.data.row_name,
          rackCount: response.data.rack_total,
          perRack: response.data.tiles_per_rack,
          size: response.data.size,
          shed: response.data.shed_id,
          totalBoxes: response.data.tiles_per_box || 0,
          boxesPerRack: response.data.boxes_per_rack || 0,
        
          rack_count: response.data.rack_count || []
        };
      }
      return null;
    } catch (error) {
      console.error("Error editing rack:", error);
      hideLoader();
      throw error;
    }
  };

  // Delete rack with API
  const deleteRack = async (id) => {
    try {
      showLoader();
      const response = await deleteRackData(id);
      if (response?.status) {
        setRacks(prev => prev.filter(r => r.id !== id));
        setRackSeats(prev => {
          const newSeats = { ...prev };
          delete newSeats[id];
          return newSeats;
        });
      }
      hideLoader();
      return response;
    } catch (error) {
      console.error("Error deleting rack:", error);
      hideLoader();
      throw error;
    }
  };

  // Initialize seats using actual rack_count from API
  const initializeSeats = (rack) => {
    if (!rack.id || !rack.rack_count || rack.rack_count.length === 0) return;


    console.log("rackFromContext",rack)
    
    const seats = rack.rack_count.map(rackItem => ({
      id: `rack-${rack.id}-${rackItem.id}`,
      rackId: rackItem.id,
      order_details: rackItem.order_details,
      rackNumber: rackItem.id,
      rackName: rackItem.rack_name || `${rack.laneName}-${rackItem.id}`,
      status: rackItem.status || "available",
      occupiedBy: rackItem.occupied_by || "",
      orderId: rackItem.order_id || null,
      tilesPerRack: rack.perRack,
      calculatedVal: rack.perRack
    }));
    
    setRackSeats(prev => ({ 
      ...prev, 
      [rack.id]: [seats]
    }));
  };

  // Update single seat status
  const updateSingleSeat = (laneId, rackIndex, seatIndex, status, occupiedBy, orderId = null) => {
    setRackSeats((prev) => {
      const laneSeats = [...prev[laneId]];
      const rackRow = [...laneSeats[rackIndex]];
      rackRow[seatIndex] = { 
        ...rackRow[seatIndex], 
        status, 
        occupiedBy,
        orderId 
      };
      laneSeats[rackIndex] = rackRow;
      return { ...prev, [laneId]: laneSeats };
    });
    
    // Update racks state to keep in sync
    setRacks(prevRacks => 
      prevRacks.map(rack => {
        if (rack.id === laneId && rack.rack_count) {
          const updatedRackCount = [...rack.rack_count];
          if (updatedRackCount[rackIndex]) {
            updatedRackCount[rackIndex] = {
              ...updatedRackCount[rackIndex],
              status: status,
              occupied_by: occupiedBy,
              order_id: orderId
            };
          }
          return { ...rack, rack_count: updatedRackCount };
        }
        return rack;
      })
    );
  };

  // Bulk update seats
  const bulkUpdateSeats = (laneId, status, occupiedBy = "") => {
    setRackSeats((prev) => {
      if (!prev[laneId]) return prev;
      const updated = prev[laneId].map((row) =>
        row.map((seat) => ({
          ...seat,
          status,
          occupiedBy: status === "occupied" ? occupiedBy || "Bulk Item" : "",
          orderId: status === "occupied" ? "bulk" : null
        }))
      );
      return { ...prev, [laneId]: updated };
    });
    
    // Update racks state
    setRacks(prevRacks => 
      prevRacks.map(rack => {
        if (rack.id === laneId && rack.rack_count) {
          const updatedRackCount = rack.rack_count.map(rackItem => ({
            ...rackItem,
            status: status,
            occupied_by: status === "occupied" ? occupiedBy || "Bulk Item" : "",
            order_id: status === "occupied" ? "bulk" : null
          }));
          return { ...rack, rack_count: updatedRackCount };
        }
        return rack;
      })
    );
  };

  // Auto-select racks based on order pieces
  const autoSelectRacks = (laneId, piecesNeeded, piecesPerRack) => {
    const racksNeeded = Math.ceil(piecesNeeded / piecesPerRack);
    const selectedRacks = [];
    
    setRackSeats((prev) => {
      if (!prev[laneId]) return prev;
      
      const laneSeats = [...prev[laneId]];
      let remainingRacks = racksNeeded;
      
      laneSeats[0].forEach((seat, index) => {
        if (remainingRacks > 0 && seat.status === 'available') {
          seat.status = 'selected';
          selectedRacks.push({
            rackId: seat.rackId,
            rackNumber: seat.rackNumber
          });
          remainingRacks--;
        }
      });
      
      return { ...prev, [laneId]: laneSeats };
    });
    
    return selectedRacks;
  };

  // Load data on mount
  useEffect(() => {
    fetchAllRacks();
    fetchingShed();
  }, [triggerRefresh]);

  return (
    <RackContext.Provider
      value={{
        racks,
        rackSeats,
        shed,
        shedLayouts,
        addRack,
        updateRack,
        deleteRack,
        editRack,
        fetchAllRacks,
        fetchingShed,
        updateSingleSeat,
        bulkUpdateSeats,
        autoSelectRacks,
        initializeSeats,
        process,
        setProcess,
        fetchingProcess,
        fetchOrderList,
        orderList,
        setTriggerRefresh
      }}
    >
      {children}
    </RackContext.Provider>
  );
};